import { useState, useCallback } from 'react';
import { Icon } from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { TriggerType } from './TriggerType';
import {
  WebhookTriggerDialog,
  useCreateWebhookTrigger,
  type TInputMappings,
  type ITriggerFieldGroup,
  type TConfigurationValues,
  type IFilterCondition,
} from '../WebhookTriggerDialog';
import type { IWebhookTriggerProps } from './types';
import { useResumeTriggerConnection } from '@/modules/triggers/hooks/use-resume-trigger-connection';

/**
 * WebhookTrigger Component
 *
 * Handles the webhook trigger type selection and configuration.
 * Encapsulates the webhook dialog and trigger creation logic.
 *
 * Flow:
 * 1. User clicks the webhook trigger type button
 * 2. Opens WebhookTriggerDialog for configuration
 * 3. On save, creates trigger instance in BDK
 * 4. Sets automations for the trigger instance in BDK
 * 5. Then creates automation trigger in Grimoire with input mappings
 *
 * @param props - Component props
 * @returns TriggerType component for webhook with integrated dialog
 */
export function WebhookTrigger({
  inputSpecs,
  automationId,
  isDraft = true,
  presentation = 'card',
  onOpen,
  open,
  onOpenChange,
  hideTrigger = false,
}: IWebhookTriggerProps) {
  const { t } = useTranslation('automation-inputs');

  // Webhook dialog open state
  const {
    value: isWebhookDialogOpen,
    setTrue: openWebhookDialog,
    setValue: setIsWebhookDialogOpen,
  } = useBoolean(false);

  // Controlled state for webhook dialog
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedIntegration, setSelectedIntegration] = useState<
    string | undefined
  >();
  const [selectedIntegrationVersion, setSelectedIntegrationVersion] = useState<
    string | undefined
  >();
  const [selectedConnection, setSelectedConnection] = useState<
    string | undefined
  >();
  const [selectedEvent, setSelectedEvent] = useState<string | undefined>();
  const [inputMappings, setInputMappings] = useState<TInputMappings>({});
  const [triggerFieldGroups, setTriggerFieldGroups] = useState<
    ITriggerFieldGroup[]
  >([]);
  const [configurationValues, setConfigurationValues] =
    useState<TConfigurationValues>({});
  const [filterConditions, setFilterConditions] = useState<IFilterCondition[]>(
    [],
  );

  // Use the webhook trigger creation hook
  const { createWebhookTrigger, isLoading } = useCreateWebhookTrigger({
    automationId,
    isDraft,
    onSuccess: () => handleDialogOpenChange(false),
  });

  /**
   * Resets dialog state to initial values for create mode
   */
  const resetDialogState = useCallback(() => {
    setCurrentStep(1);
    setSelectedIntegration(undefined);
    setSelectedIntegrationVersion(undefined);
    setSelectedConnection(undefined);
    setSelectedEvent(undefined);
    setInputMappings({});
    setTriggerFieldGroups([]);
    setConfigurationValues({});
    setFilterConditions([]);
  }, []);

  useResumeTriggerConnection({
    kind: 'webhook',
    onResume: useCallback(
      ({ integrationName, integrationVersion, connectionId }) => {
        setCurrentStep(1);
        setSelectedIntegration(integrationName);
        setSelectedIntegrationVersion(integrationVersion);
        setSelectedConnection(connectionId);
        setIsWebhookDialogOpen(true);
        onOpenChange?.(true);
      },
      [setIsWebhookDialogOpen, onOpenChange],
    ),
  });

  const handleDialogOpenChange = (open: boolean) => {
    setIsWebhookDialogOpen(open);
    onOpenChange?.(open);
    if (!open) {
      resetDialogState();
    }
  };

  const handleOpen = () => {
    onOpen?.();
    openWebhookDialog();
    onOpenChange?.(true);
  };

  return (
    <>
      {!hideTrigger && (
        <TriggerType
          icon={
            <Icon
              type="Webhook"
              size="lg"
              strokeWidth={1.5}
              className="text-ring"
            />
          }
          title={t('triggerTypes.webhook.title')}
          description={t('triggerTypes.webhook.description')}
          onClick={handleOpen}
          presentation={presentation}
        />
      )}

      {/* Webhook Trigger Dialog */}
      <WebhookTriggerDialog
        isOpen={open ?? isWebhookDialogOpen}
        onOpenChange={handleDialogOpenChange}
        inputSpecs={inputSpecs}
        onSave={createWebhookTrigger}
        isLoading={isLoading}
        // Controlled state props
        currentStep={currentStep}
        onStepChange={setCurrentStep}
        selectedIntegration={selectedIntegration}
        onSelectedIntegrationChange={setSelectedIntegration}
        selectedIntegrationVersion={selectedIntegrationVersion}
        onSelectedIntegrationVersionChange={setSelectedIntegrationVersion}
        selectedConnection={selectedConnection}
        onSelectedConnectionChange={setSelectedConnection}
        selectedEvent={selectedEvent}
        onSelectedEventChange={setSelectedEvent}
        inputMappings={inputMappings}
        onInputMappingsChange={setInputMappings}
        triggerFieldGroups={triggerFieldGroups}
        onTriggerFieldGroupsChange={setTriggerFieldGroups}
        configurationValues={configurationValues}
        onConfigurationValuesChange={setConfigurationValues}
        filterConditions={filterConditions}
        onFilterConditionsChange={setFilterConditions}
      />
    </>
  );
}
